'use client';

import { REGISTRY_BLOCK } from '@web-studio/shared-schemas';
import { EditableContent } from '../../edit/EditableContent';
import { defaultReservationFields } from '../../lib/defaultFormFields';
import { parseFloorTables } from '../../lib/parseFloorTables';
import { parseFormFields } from '../../lib/parseFormFields';
import { pickBlockField } from '../../lib/localized';
import { pickChrome } from '../../lib/siteChromeCopy';
import type { BlockRenderProps } from '../../types';
import { ConsentCheckbox, FloorTableSelect, FormFieldControl } from '../forms/FormFieldControls';
import { SiteLeadForm } from '../forms/SiteLeadForm';

export function ReservationFormEmbed({
  block,
  locale,
  isActive,
  activeEditTarget,
  editable,
}: BlockRenderProps) {
  const heading = pickBlockField(block.content, 'heading', locale);
  const supporting = pickBlockField(block.content, 'supporting', locale);
  const consent = pickBlockField(block.content, 'consent', locale);
  const ctaLabel = pickBlockField(block.content, 'ctaLabel', locale);
  const note = pickBlockField(block.content, 'note', locale);
  const tableLabel = pickBlockField(block.content, 'tableLabel', locale);
  const target = isActive ? activeEditTarget : null;
  const operational = parseFormFields(pickBlockField(block.content, 'formFields', locale));
  const fields =
    operational.length > 0
      ? operational.filter((field) => field.name !== 'consent')
      : defaultReservationFields(locale);
  const consentFromFields = operational.find((field) => field.name === 'consent');
  const consentLabel =
    consentFromFields?.label || consent || pickChrome('consentFallback', locale);
  const floorTables = parseFloorTables(pickBlockField(block.content, 'floorTables', locale));
  const showCapacity =
    block.dataBinding?.kind === 'floor_plan' ? block.dataBinding.showCapacity : true;
  const formKey =
    block.dataBinding?.kind === 'form' ? block.dataBinding.formKey : 'reservation';
  const isEditorial =
    block.registryBlockKey === REGISTRY_BLOCK.presentation.reservationEditorialForm;

  return (
    <section
      className={[
        'ws-reservation',
        isEditorial ? 'ws-reservation--editorial' : '',
        isActive ? 'ws-block--active' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      data-registry-key={block.registryBlockKey}
      id="reserve"
    >
      <div className="ws-reservation__inner">
        <EditableContent
          as="h2"
          fieldKey="heading"
          editable={editable}
          activeEditTarget={target}
          className="ws-reservation__title"
        >
          {heading}
        </EditableContent>
        <EditableContent
          fieldKey="supporting"
          editable={editable}
          activeEditTarget={target}
          className="ws-reservation__subtitle"
        >
          {supporting}
        </EditableContent>
        <SiteLeadForm
          locale={locale}
          formKey={formKey}
          editable={editable}
          className="ws-reservation__form"
          statusChrome={{ success: 'reservationStatusSuccess' }}
        >
          <FloorTableSelect
            tables={floorTables}
            showCapacity={showCapacity}
            locale={locale}
            label={tableLabel}
            fieldClassName="ws-reservation__field"
            editable={editable}
            activeEditTarget={target}
          />
          {fields.map((field) => (
            <FormFieldControl
              key={field.name}
              field={field}
              locale={locale}
              fieldClassName="ws-reservation__field"
              editable={editable}
              activeEditTarget={target}
            />
          ))}
          {consentFromFields ? (
            <ConsentCheckbox label={consentFromFields.label} className="ws-form__consent" />
          ) : (
            <label className="ws-form__consent">
              <input type="checkbox" name="consent" required />
              <EditableContent
                as="span"
                fieldKey="consent"
                editable={editable}
                activeEditTarget={target}
              >
                {consentLabel}
              </EditableContent>
            </label>
          )}
          <EditableContent
            as="button"
            type="submit"
            fieldKey="ctaLabel"
            editable={editable}
            activeEditTarget={target}
            className="ws-reservation__submit"
          >
            {ctaLabel || pickChrome('submitFallbackReservation', locale)}
          </EditableContent>
        </SiteLeadForm>
        {note || editable ? (
          <EditableContent
            fieldKey="note"
            editable={editable}
            activeEditTarget={target}
            className="ws-reservation__note"
          >
            {note}
          </EditableContent>
        ) : null}
      </div>
    </section>
  );
}
